import { Component, OnInit } from '@angular/core';
import { AdminService, AdminDashboardStats, SubscriptionStatus } from '../../services/admin.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit {
  activeTab: 'dashboard' | 'users' | 'professionals' | 'subscriptions' | 'payments' = 'dashboard';
  
  // Dashboard
  dashboardStats?: AdminDashboardStats;
  
  // Usuários
  users: any[] = [];
  filteredUsers: any[] = [];
  userSearchTerm = '';
  
  // Profissionais
  professionals: any[] = [];
  filteredProfessionals: any[] = [];
  professionalSearchTerm = '';
  
  // Assinaturas
  subscriptions: any[] = [];
  filteredSubscriptions: any[] = [];
  subscriptionFilter: SubscriptionStatus | null = null;
  
  // Pagamentos
  pendingPayments: any[] = [];
  
  loading = false;
  
  // Modals
  showUserDetailsModal = false;
  selectedUser: any = null;

  constructor(
    private adminService: AdminService,
    private notification: NotificationService
  ) {}

  ngOnInit() {
    this.loadDashboard();
  }

  setActiveTab(tab: 'dashboard' | 'users' | 'professionals' | 'subscriptions' | 'payments') {
    this.activeTab = tab;
    
    switch(tab) {
      case 'dashboard':
        this.loadDashboard();
        break;
      case 'users':
        this.loadUsers();
        break;
      case 'professionals':
        this.loadProfessionals();
        break;
      case 'subscriptions':
        this.loadSubscriptions();
        break;
      case 'payments':
        this.loadPendingPayments();
        break;
    }
  }

  // ===== DASHBOARD =====
  loadDashboard() {
    this.loading = true;
    this.adminService.getDashboardStats().subscribe({
      next: (stats) => {
        this.dashboardStats = stats;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar dashboard', error);
        this.loading = false;
      }
    });
  }

  // ===== USUÁRIOS =====
  loadUsers() {
    this.loading = true;
    this.adminService.getAllUsers().subscribe({
      next: (result) => {
        const users = result?.items ?? result ?? [];
        this.users = users;
        this.filteredUsers = users;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar usuários', error);
        this.loading = false;
      }
    });
  }

  filterUsers() {
    const term = this.userSearchTerm.toLowerCase();
    this.filteredUsers = this.users.filter(u =>
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term)
    );
  }

  toggleUserStatus(user: any) {
    if (!confirm(`Deseja ${user.isActive ? 'desativar' : 'ativar'} ${user.name}?`)) {
      return;
    }

    this.adminService.toggleUserStatus(user.id, !user.isActive).subscribe({
      next: () => {
        user.isActive = !user.isActive;
        this.notification.success('Sucesso', `Usuário ${user.isActive ? 'ativado' : 'desativado'} com sucesso!`);
      },
      error: (error) => {
        console.error('Erro ao alterar status', error);
        this.notification.error('Erro', 'Erro ao alterar status do usuário');
      }
    });
  }

  deleteUser(user: any) {
    if (!confirm(`Deseja realmente deletar ${user.name}? Esta ação não pode ser desfeita.`)) {
      return;
    }

    this.adminService.deleteUser(user.id).subscribe({
      next: () => {
        this.loadUsers();
        this.notification.success('Sucesso', 'Usuário deletado com sucesso!');
      },
      error: (error) => {
        console.error('Erro ao deletar usuário', error);
        this.notification.error('Erro', 'Erro ao deletar usuário');
      }
    });
  }

  viewUserDetails(user: any) {
    this.adminService.getUserDetails(user.id).subscribe({
      next: (details) => {
        this.selectedUser = details;
        this.showUserDetailsModal = true;
      },
      error: (error) => {
        console.error('Erro ao carregar detalhes', error);
      }
    });
  }

  // ===== PROFISSIONAIS =====
  loadProfessionals() {
    this.loading = true;
    this.adminService.getAllProfessionals().subscribe({
      next: (result) => {
        const professionals = result?.items ?? result ?? [];
        this.professionals = professionals;
        this.filteredProfessionals = professionals;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar profissionais', error);
        this.loading = false;
      }
    });
  }

  filterProfessionals() {
    const term = this.professionalSearchTerm.toLowerCase();
    this.filteredProfessionals = this.professionals.filter(p =>
      p.name.toLowerCase().includes(term) ||
      (p.specialty && p.specialty.toLowerCase().includes(term))
    );
  }

  toggleProfessionalStatus(professional: any) {
    if (!confirm(`Deseja ${professional.isActive ? 'desativar' : 'ativar'} ${professional.name}?`)) {
      return;
    }

    this.adminService.toggleProfessionalStatus(professional.id, !professional.isActive).subscribe({
      next: () => {
        professional.isActive = !professional.isActive;
        this.notification.success('Sucesso', `Profissional ${professional.isActive ? 'ativado' : 'desativado'} com sucesso!`);
      },
      error: (error) => {
        console.error('Erro ao alterar status', error);
        this.notification.error('Erro', 'Erro ao alterar status do profissional');
      }
    });
  }

  // ===== ASSINATURAS =====
  loadSubscriptions(status?: SubscriptionStatus) {
    this.loading = true;
    this.subscriptionFilter = status || null;
    
    this.adminService.getAllSubscriptions(status).subscribe({
      next: (result) => {
        const subscriptions = result?.items ?? result ?? [];
        this.subscriptions = subscriptions;
        this.filteredSubscriptions = subscriptions;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar assinaturas', error);
        this.loading = false;
      }
    });
  }

  cancelSubscription(subscription: any) {
    const reason = prompt('Motivo do cancelamento:');
    if (!reason) return;

    this.adminService.cancelSubscription(subscription.id, reason).subscribe({
      next: () => {
        this.loadSubscriptions();
        this.notification.success('Sucesso', 'Assinatura cancelada!');
      },
      error: (error) => {
        console.error('Erro ao cancelar', error);
        this.notification.error('Erro', 'Erro ao cancelar assinatura');
      }
    });
  }

  activateSubscription(subscription: any) {
    if (!confirm('Deseja ativar esta assinatura?')) return;

    this.adminService.activateSubscription(subscription.id).subscribe({
      next: () => {
        this.loadSubscriptions();
        this.notification.success('Sucesso', 'Assinatura ativada!');
      },
      error: (error) => {
        console.error('Erro ao ativar', error);
        this.notification.error('Erro', 'Erro ao ativar assinatura');
      }
    });
  }

  // ===== PAGAMENTOS =====
  loadPendingPayments() {
    this.loading = true;
    this.adminService.getPendingPayments().subscribe({
      next: (result) => {
        // backend retorna PagedResult — extrair items
        this.pendingPayments = result?.items ?? result ?? [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar pagamentos', error);
        this.loading = false;
      }
    });
  }

  confirmPayment(payment: any) {
    if (!confirm(`Confirmar pagamento de R$ ${payment.amount?.toFixed(2).replace('.', ',')} do profissional ${payment.professionalName}?`)) return;

    this.adminService.confirmPayment(payment.id).subscribe({
      next: () => {
        this.notification.success('Pagamento Confirmado', `Pagamento de ${payment.professionalName} confirmado. Assinatura reativada.`);
        this.loadPendingPayments();
      },
      error: () => this.notification.error('Erro', 'Não foi possível confirmar o pagamento.')
    });
  }

  // ===== HELPERS =====
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('pt-BR');
  }

  getStatusBadgeClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Trial': 'badge bg-info',
      'Active': 'badge bg-success',
      'PastDue': 'badge bg-warning',
      'Suspended': 'badge bg-danger',
      'Cancelled': 'badge bg-secondary',
      'Expired': 'badge bg-dark'
    };
    return statusMap[status] || 'badge bg-secondary';
  }

  getStatusLabel(status: number): string {
    const statusLabels: { [key: number]: string } = {
      1: 'Trial',
      2: 'Ativo',
      3: 'Atrasado',
      4: 'Suspenso',
      5: 'Cancelado',
      6: 'Expirado'
    };
    return statusLabels[status] || 'Desconhecido';
  }

  isVencido(dueDate: string | Date): boolean {
    return new Date(dueDate) < new Date();
  }

  closeModals() {
    this.showUserDetailsModal = false;
    this.selectedUser = null;
  }
}
